import CloseCircleXIcon from '@assets/icons/close-circle-x-large.svg';
import i18n from '@/utils/i18n';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  handleWebKeyPress,
  Key,
} from '@platform/shortcuts/utils/keyboard-platform';
import {
  NativeSyntheticEvent,
  Pressable,
  TextInput,
  TextInputKeyPressEventData,
  TextInputSubmitEditingEventData,
} from 'react-native';
import { SearchInput as SearchInputAtom } from '@usepepper/ui/atoms/search-input';

const ORDER_ENTRY_SEARCH_RESET_BUTTON_SIZE = 24;
export const SEARCH_DEBOUNCE_MS = 150;

interface SearchInputProps {
  autoFocus?: boolean;
  onBlur?: () => void;
  onEscapePress?: () => void;
  onFocus?: () => void;
  onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
  onSearchChange: (text: string) => void;
  onSubmitEditing?: (
    e: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
  ) => void;
  placeholderText: string;
  searchText: string;
}

export interface SearchInputRef {
  blur: () => void;
  focus: () => void;
}

export const SearchInput = forwardRef<SearchInputRef, SearchInputProps>(
  (
    {
      autoFocus,
      onBlur,
      onEscapePress,
      onFocus,
      onKeyPress,
      onSearchChange,
      onSubmitEditing,
      placeholderText,
      searchText,
    },
    ref,
  ) => {
    const [styles] = useStyles();
    const inputRef = useRef<TextInput>(null);

    useImperativeHandle(ref, () => ({
      blur: () => inputRef.current?.blur(),
      focus: () => inputRef.current?.focus(),
    }));

    const handleClearSearch = useCallback(() => {
      onSearchChange('');
    }, [onSearchChange]);

    const isClearHidden = searchText.length === 0;

    const handleKeyPress = useCallback(
      (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        // Call custom onKeyPress handler first (allows overriding behavior)
        if (onKeyPress) {
          onKeyPress(e);
          // If the handler prevented default, don't process further
          if (
            typeof e.isDefaultPrevented === 'function' &&
            e.isDefaultPrevented()
          ) {
            return;
          }
        }

        if (e.nativeEvent.key === Key.Escape) {
          onEscapePress?.();
          inputRef.current?.blur();
          return;
        }
        handleWebKeyPress(e);
      },
      [onEscapePress, onKeyPress],
    );

    return (
      <SearchInputAtom
        autoFocus={autoFocus}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyPress={handleKeyPress}
        onSearch={onSearchChange}
        onSubmitEditing={onSubmitEditing}
        placeholderText={placeholderText}
        ref={inputRef}
        rightElement={
          <Pressable
            accessibilityElementsHidden={isClearHidden}
            accessibilityLabel={i18n.t('clear_search')}
            accessibilityRole="button"
            aria-hidden={isClearHidden}
            disabled={isClearHidden}
            hitSlop={20}
            importantForAccessibility={
              isClearHidden ? 'no-hide-descendants' : 'auto'
            }
            onPress={handleClearSearch}
            style={[
              styles.clearButton,
              isClearHidden && styles.clearButtonNotClickable,
            ]}
            tabIndex={-1}
          >
            <CloseCircleXIcon
              height={ORDER_ENTRY_SEARCH_RESET_BUTTON_SIZE}
              width={ORDER_ENTRY_SEARCH_RESET_BUTTON_SIZE}
            />
          </Pressable>
        }
        searchText={searchText}
        selectOnFocus
      />
    );
  },
);

const useStyles = createStyles(() => ({
  clearButton: {
    borderRadius: 32,
  },
  clearButtonNotClickable: {
    opacity: 0,
    pointerEvents: 'none',
  },
}));
